import { optimoPrivateApi, optimoPrivateApiFormData } from '../../axios/axiosInstance'
import { endpoints } from '../../endpoints'

import type {
    IListUsersParams,
    IListUsersResponse,
    IUserDetailsResponse,
    IUserByEmailResponse,
    IMyTeamParams,
    IMyTeamResponse,
    ICurrentUserResponse,
    IProfileResponse,
    IUpdateProfilePayload,
    IUpdateProfileResponse,
    IProfilePictureUploadResponse,
    IProfilePictureRemoveResponse,
    IInviteUserPayload,
    IInviteUserResponse,
} from './usersActions.types'

/**
 * List users with pagination and filtering
 */
export const listUsers = async (params?: IListUsersParams): Promise<IListUsersResponse> => {
    const searchParams = new URLSearchParams()

    if (params?.page) {
        searchParams.append('page', params.page.toString())
    }
    if (params?.page_size) {
        searchParams.append('page_size', params.page_size.toString())
    }
    if (params?.search) {
        searchParams.append('search', params.search)
    }
    if (params?.department) {
        searchParams.append('department', params.department)
    }
    if (params?.division) {
        searchParams.append('division', params.division)
    }
    if (params?.job_title) {
        searchParams.append('job_title', params.job_title)
    }
    if (params?.location) {
        searchParams.append('location', params.location)
    }
    if (params?.employment_status) {
        searchParams.append('employment_status', params.employment_status)
    }
    if (params?.is_people_manager !== undefined) {
        searchParams.append('is_people_manager', params.is_people_manager.toString())
    }
    if (params?.is_active !== undefined) {
        searchParams.append('is_active', params.is_active.toString())
    }
    if (params?.ordering) {
        searchParams.append('ordering', params.ordering)
    }
    if (params?.count_only) {
        searchParams.append('count_only', params.count_only.toString())
    }

    const queryString = searchParams.toString()
    const url = queryString ? `${endpoints.users.list}?${queryString}` : endpoints.users.list

    const response = await optimoPrivateApi.get<IListUsersResponse>(url)
    return response.data
}

/**
 * Get user details by ID
 */
export const getUserDetails = async (userId: string): Promise<IUserDetailsResponse> => {
    const response = await optimoPrivateApi.get<IUserDetailsResponse>(`${endpoints.users.details}/${userId}`)
    return response.data
}

/**
 * Get user by email address
 */
export const getUserByEmail = async (email: string): Promise<IUserByEmailResponse> => {
    // URL encode the email to handle special characters
    const encodedEmail = encodeURIComponent(email)
    const response = await optimoPrivateApi.get<IUserByEmailResponse>(`${endpoints.users.byEmail}/${encodedEmail}`)
    return response.data
}

/**
 * Get manager's team members (direct and indirect reports)
 */
export const getMyTeam = async (params?: IMyTeamParams): Promise<IMyTeamResponse> => {
    const searchParams = new URLSearchParams()

    if (params?.page) {
        searchParams.append('page', params.page.toString())
    }
    if (params?.page_size) {
        searchParams.append('page_size', params.page_size.toString())
    }
    if (params?.include_indirect !== undefined) {
        searchParams.append('include_indirect', params.include_indirect.toString())
    }
    if (params?.is_active !== undefined) {
        searchParams.append('is_active', params.is_active.toString())
    }
    if (params?.count_only) {
        searchParams.append('count_only', params.count_only.toString())
    }

    const queryString = searchParams.toString()
    const url = queryString ? `${endpoints.users.myTeam}?${queryString}` : endpoints.users.myTeam

    const response = await optimoPrivateApi.get<IMyTeamResponse>(url)
    return response.data
}

/**
 * Get current authenticated user information
 */
export const getCurrentUser = async (): Promise<ICurrentUserResponse> => {
    const response = await optimoPrivateApi.get<ICurrentUserResponse>(endpoints.auth.user)
    return response.data
}

/**
 * Get user profile information
 */
export const getUserProfile = async (userUuid: string): Promise<IProfileResponse> => {
    try {
        const response = await optimoPrivateApi.get<IProfileResponse>(`${endpoints.users.profile}/${userUuid}/profile`)
        return response.data
    } catch (error) {
        // Add context to profile API errors for better debugging
        if (error instanceof Error) {
            error.message = `Profile API Error: ${error.message} (UUID: ${userUuid})`
        }
        throw error
    }
}

/**
 * Update user profile information
 */
export const updateUserProfile = async (
    userUuid: string,
    payload: IUpdateProfilePayload,
): Promise<IUpdateProfileResponse> => {
    const response = await optimoPrivateApi.patch<IUpdateProfileResponse>(
        `${endpoints.users.profile}/${userUuid}/profile`,
        payload,
    )
    return response.data
}

/**
 * Upload user profile picture
 */
export const uploadProfilePicture = async (userUuid: string, file: File): Promise<IProfilePictureUploadResponse> => {
    try {
        const formData = new FormData()
        formData.append('file', file)

        const response = await optimoPrivateApiFormData.post<IProfilePictureUploadResponse>(
            `${endpoints.users.profilePicture}/${userUuid}/profile/picture`,
            formData,
        )

        return response.data
    } catch (error) {
        // Add context to upload API errors for better debugging
        if (error instanceof Error) {
            error.message = `Profile Picture Upload Error: ${error.message} (UUID: ${userUuid}, File: ${file.name}, Size: ${file.size})`
        }
        throw error
    }
}

/**
 * Remove user profile picture
 */
export const removeProfilePicture = async (userUuid: string): Promise<IProfilePictureRemoveResponse> => {
    // Send POST request with no body to remove picture (Optimo API spec)
    const response = await optimoPrivateApi.post<IProfilePictureRemoveResponse>(
        `${endpoints.users.profilePicture}/${userUuid}/profile/picture`,
    )
    return response.data
}

/**
 * Invite a new user to the organization
 */
export const inviteUser = async (payload: IInviteUserPayload): Promise<IInviteUserResponse> => {
    const response = await optimoPrivateApi.post<IInviteUserResponse>(endpoints.auth.invite, payload)
    return response.data
}
